import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { useAccessRestrictionStore } from '@/stores/access-restriction.store';
import { accessRestrictionReasonForCode } from '@/lib/access-restriction';
import { refreshTenantAccessToken } from '@/lib/tenant-token-refresh';

const BASE_URL = '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  withCredentials: true, // required for HTTP-only refresh token cookie
});

// Attach access token from in-memory store to every request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token !== null) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Unwrap the success envelope { data, meta } → return data directly
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data as Record<string, unknown> | null | undefined;
    if (body !== null && body !== undefined && typeof body === 'object' && 'data' in body) {
      return { ...response, data: body['data'] };
    }
    return response;
  },
  async (error: unknown) => {
    // ── Access restriction (403 with a known restriction code) ──────────────
    //
    // `TenantActiveGuard` / `SubscriptionGuard` refused the request because this
    // workspace may not be used — not because the caller is unauthenticated and
    // not because they lack a permission. Record the reason so
    // <AccessRestrictionRedirect> can route to the dedicated screen instead of
    // every page rendering its own "could not load" strip.
    //
    // Three properties this must keep:
    //
    //  1. **Matched on the code, never the message.** An ordinary 403 (RBAC, the
    //     login lockout, CAPTCHA_REQUIRED) carries no restriction code, so it
    //     falls straight through to the untouched reject below.
    //  2. **The session is NOT cleared.** The JWT is valid and the user is
    //     genuinely signed in; clearing would bounce them to the login page and
    //     misreport an authorization state as a credential failure. The backend
    //     keeps refusing every protected request either way.
    //  3. **The error is still rejected.** Callers see the failure exactly as
    //     before; nothing is swallowed.
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      const code = (error.response.data as { error?: { code?: string } } | undefined)?.error?.code;
      const reason = accessRestrictionReasonForCode(code);
      if (reason !== null) {
        useAccessRestrictionStore.getState().restrict(reason);
      }
    }

    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Prevent retry loops — if already retried once, clear auth and bail
      if (config._retry) {
        useAuthStore.getState().clearAuth();
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      // Optimistic check — if no refresh token in store, nothing to try
      if (!useAuthStore.getState().refreshToken) {
        useAuthStore.getState().clearAuth();
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      config._retry = true;
      try {
        const newAccessToken = await refreshTenantAccessToken();
        config.headers.set('Authorization', `Bearer ${newAccessToken}`);
        return await apiClient.request(config);
      } catch {
        useAuthStore.getState().clearAuth();
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

// ── Dev-only debug logging ──────────────────────────────────────────────────
if (import.meta.env.DEV) {
  apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const method = config.method?.toUpperCase() ?? 'REQ';
    if (config.data instanceof FormData) {
      const fields: string[] = [];
      config.data.forEach((v, k) => {
        fields.push(
          `${k}=${v instanceof File ? `File(name=${v.name} type=${v.type} size=${v.size}B)` : String(v)}`,
        );
      });
      console.debug(`[API→] ${method} ${config.url ?? ''} [${fields.join(' | ')}]`);
    } else {
      console.debug(`[API→] ${method} ${config.url ?? ''}`);
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      console.debug(`[API←] ${response.status} ${response.config.url ?? ''}`);
      return response;
    },
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        console.error(
          `[API✗] ${error.response?.status ?? 'ERR'} ${error.config?.url ?? ''}`,
          error.response?.data ?? error.message,
        );
      }
      return Promise.reject(error);
    },
  );
}
